import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, clearSession, getAccessToken, loadStoredUser, setSession } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(loadStoredUser());
  const [isApprover, setIsApprover] = useState(false);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [booting, setBooting] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!getAccessToken()) {
        setBooting(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) {
          setUser(data.user);
          setIsApprover(!!data.is_approver);
          setIsPlatformAdmin(!!data.is_platform_admin);
          setSession({ user: data.user });
        }
      } catch (e) {
        if (!cancelled) {
          clearSession();
          setUser(null);
          setIsApprover(false);
          setIsPlatformAdmin(false);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async ({ email, password, tenant_slug, mfa_code }) => {
    const { data } = await api.post("/auth/login", { email, password, tenant_slug, mfa_code });
    if (data.mfa_required) return { mfa_required: true, identity_id: data.identity_id };
    // Phase 22 — multi-tenant identity may return a temp_token that the
    // caller cashes in at /auth/select-tenant.
    if (data.needs_tenant_selection) {
      return {
        needs_tenant_selection: true,
        temp_token: data.temp_token,
        memberships: data.memberships,
        default_tenant_id: data.default_tenant_id,
        identity: data.identity,
      };
    }
    setSession(data);
    setUser(data.user);
    try {
      const { data: me } = await api.get("/auth/me");
      setIsApprover(!!me.is_approver);
      setIsPlatformAdmin(!!me.is_platform_admin);
    } catch (_e) {}
    return { user: data.user };
  }, []);

  // Phase 22 — cash in a multi-select temp_token.
  const selectTenant = useCallback(async ({ temp_token, tenant_id }) => {
    const { data } = await api.post("/auth/select-tenant", { temp_token, tenant_id });
    setSession(data);
    setUser(data.user);
    try {
      const { data: me } = await api.get("/auth/me");
      setIsApprover(!!me.is_approver);
      setIsPlatformAdmin(!!me.is_platform_admin);
    } catch (_e) {}
    return data;
  }, []);

  // Phase 22 — switch tenant while authenticated.
  const switchTenant = useCallback(async ({ tenant_id }) => {
    const { data } = await api.post("/auth/switch-tenant", { tenant_id });
    setSession(data);
    setUser(data.user);
    try {
      const { data: me } = await api.get("/auth/me");
      setIsApprover(!!me.is_approver);
      setIsPlatformAdmin(!!me.is_platform_admin);
    } catch (_e) {}
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setSession(data);
    setUser(data.user);
    setIsApprover(false);
    setIsPlatformAdmin(false);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem("clockin.refresh");
      if (rt) await api.post("/auth/logout", { refresh_token: rt });
    } catch (_e) {}
    clearSession();
    setUser(null);
    setIsApprover(false);
    setIsPlatformAdmin(false);
  }, []);

  const refreshMe = useCallback(async () => {
    const { data } = await api.get("/auth/me");
    setUser(data.user);
    setIsApprover(!!data.is_approver);
    setIsPlatformAdmin(!!data.is_platform_admin);
    setSession({ user: data.user });
    return data.user;
  }, []);

  const hasRole = useCallback(
    (roles) => {
      if (!user) return false;
      const wanted = Array.isArray(roles) ? roles : [roles];
      return wanted.some((r) => (user.roles || []).includes(r));
    },
    [user]
  );

  const value = useMemo(
    () => ({ user, isApprover, isPlatformAdmin, booting, login, signup, logout, refreshMe, hasRole, selectTenant, switchTenant }),
    [user, isApprover, isPlatformAdmin, booting, login, signup, logout, refreshMe, hasRole, selectTenant, switchTenant]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
